import { useMemo, useRef, useState } from "react";
import { activeFileSelector, openFile } from "store/features";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { CodeEditor, CodeEditorInstance, ImageViewer, isImageType } from "@sandbox/code-editor";
import { ResizablePanels } from "@sandbox/resizable-panels";
import { CodePanelsMemo } from "./CodePanels";

export const Code = () => {
  const dispatch = useAppDispatch();
  const state = useAppSelector(activeFileSelector);
  const theme = useAppSelector((state) => state.settings.theme);
  const layout = useAppSelector((state) => state.settings.layout);
  const editor = useRef<CodeEditorInstance>({});
  const [panelsVisible, setPanelsVisible] = useState(layout.statusPanels !== "closed");

  const codeEditor = useMemo(() => {
    return (
      <div style={{ width: "100%", height: "100%" }}>
        {isImageType(state?.filePath ?? "") && <ImageViewer data={state?.content ?? ""} />}
        <CodeEditor
          filePath={state?.filePath}
          readOnly={state?.activeFile?.readOnly}
          selection={state?.activeFile?.selection}
          focusMode={state?.activeFile?.focusMode}
          theme={theme}
          showMinimap={layout.editorMiniMap}
          showlineNumbers={layout.editorLineNumbers}
          codeFolding={layout.editorCodeFolding}
          onEditorMounted={(mounted) => {
            editor.current.instance = mounted?.instance
          }}
          onDefinitionOpened={(filePath, selection) => {
            dispatch(openFile({ filePath, selection, readOnly: true, filePathType: "typeDeclaration" }));
          }}
        />
      </div>);
  }, [state?.filePath, state?.activeFile?.readOnly, state?.activeFile?.selection, state?.activeFile?.focusMode, theme]);

  if (layout.statusPanels === "hidden")
    return <>{codeEditor}</>

  return (
    <ResizablePanels
      snap={true}
      hideSashOnSnap={true}
      defaultSizes={[80, 20]}
      firstPanel={{ preferredSize: "80%", visible: true }}
      secondPanel={{ preferredSize: "20%", minSize: 100, visible: panelsVisible, onVisibilityChange: setPanelsVisible }}
      vertical={true}
    >
      {codeEditor}
      <CodePanelsMemo editor={editor.current} onCollapsed={() => setPanelsVisible(false)} />
    </ResizablePanels>
  );
}